import { Box, Grid, Typography } from "@mui/material";
import styled from "styled-components";
import ProgressFG from "../../../components/oeeDashboard/ProgressFG";
import Timeline24H from "../../../components/oeeDashboard/Timeline24H";
import { BoxValue, PanelBody } from "../styles";
import { theme } from "../styles/theme";
import moment from "moment-timezone";
import { Tooltip } from "@mui/material";

const PanelSKUWrapper = styled(Box)`
  background: ${({ theme }) => theme.palette.background.default};
  padding: 5px;
  border-radius: 8px;
  margin-top: 10px;
`;

const LabelSku = styled(Typography)`
  font-size: clamp(16px, 2vw, 24px) !important;
  font-weight: 800 !important;
  display: flex;
  justify-content: space-evenly;
  align-items: center;

  & span:first-of-type {
    font-size: clamp(16px, 2vw, 24px) !important;
    font-weight: 500 !important;
  }
`;

const BoxSku = styled(Box)(
  ({
    color = "#a5a5a5",
    bdcolor = theme.skuBorderStatus.yellow,
  }) => ({
    border: `1px dashed ${bdcolor}`,
    borderRadius: "8px",
    padding: "5px 10px",
    width: "100%",
    "& p": {
      borderBottom: `1px dashed ${bdcolor}`,
      textAlign: "center",
      fontSize: "20px",
      fontWeight: "bold",
      color: `${color}`,
      marginBottom: "5px",
    },
    div: {
      display: "flex",
      justifyContent: "space-between",
    },
    "div>div": {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    },
    "div>div>div": {
      flexDirection: "row",
      alignItems: "center",
      minWidth: "40px",
    },
  })
);

const StatusCircle = styled(Box)(({ color = "#fff" }) => ({
  display: "inline-block !important",
  background: color,
  minWidth: "15px !important",
  height: "15px",
  borderRadius: "30px",
  marginTop: "-3px",
  marginLeft: "5px",
}));

const ValueSkg = styled("span")(({ color = "#fff" }) => ({
  fontSize: "20px",
  color: color,
  fontWeight: "bold",
  paddingRight: "5px",
}));

const formatDurationHHmm = (value) => {
  if (value === undefined || value === null || value === "NA") return "-";

  let duration;

  if (moment.isDuration(value)) {
    duration = value;
  } else if (typeof value === "string") {
    duration = moment.duration(value);
  } else if (typeof value === "number") {
    duration = moment.duration(value, "seconds");
  } else {
    return "-";
  }

  const hours = Math.floor(duration.asHours())
    .toString()
    .padStart(2, "0");

  const minutes = duration.minutes()
    .toString()
    .padStart(2, "0");

  return `${hours}:${minutes}`;
};

const getMomentFromDateTime = (value) => {
  if (!value || value === "NA") return null;

  const m = moment(
    value,
    [
      "YYYY-MM-DD HH:mm:ss",
      "YYYY-MM-DDTHH:mm:ss",
      "YYYY-MM-DD HH:mm",
      "YYYY-MM-DDTHH:mm",
    ],
    true
  );

  if (m.isValid()) return m;

  const fallback = moment(value);
  return fallback.isValid() ? fallback : null;
};

const normalizeStatus = (value) =>
  String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[-\s]/g, "_");

const calculateGroupedNonProductTime = (oeeProcess = []) => {
  const summary = {
    STOP: 0,
    CHANGE_PRD: 0,
    BREAK: 0,
    CLEANING: 0,
    MAT_SHORT: 0,
    QUALITY: 0,
    OTHER: 0,
    RD: 0,
  };

  if (!Array.isArray(oeeProcess) || oeeProcess.length === 0) {
    return summary;
  }

  oeeProcess.forEach((item) => {
    const rawStatus = item?.STATUS || item?.PROCESS;
    const statusName = normalizeStatus(rawStatus);

    const start = getMomentFromDateTime(item?.START);
    const end = getMomentFromDateTime(item?.END);

    if (!statusName || !start || !end || !end.isAfter(start)) return;

    const durationSeconds = end.diff(start, "seconds");
    if (durationSeconds <= 0) return;

    if (["NON_PRODUCTION", "STOP"].includes(statusName)) {
      summary.STOP += durationSeconds;
      return;
    }

    if (["CHANGE_PRODUCT", "CHANGE_PRD"].includes(statusName)) {
      summary.CHANGE_PRD += durationSeconds;
      return;
    }

    if (statusName === "BREAK") {
      summary.BREAK += durationSeconds;
      return;
    }

    if (statusName === "CLEANING") {
      summary.CLEANING += durationSeconds;
      return;
    }

    if (["MATERIAL_SHORTAGE", "MAT_SHORT"].includes(statusName)) {
      summary.MAT_SHORT += durationSeconds;
      return;
    }

    if (statusName === "QUALITY") {
      summary.QUALITY += durationSeconds;
      return;
    }

    if (statusName === "RD") {
      summary.RD += durationSeconds;
      return;
    }

    if (["OTHER", "OTHERS"].includes(statusName)) {
      summary.OTHER += durationSeconds;
      return;
    }
  });

  return summary;
};

const buildTimelineFullDayForSummary = (timeline = [], startTime) => {
  const s = getMomentFromDateTime(startTime);

  if (!s || !Array.isArray(timeline)) {
    return [];
  }

  let baseline = s.clone().hour(6).minute(0).second(0).millisecond(0);

  if (s.hour() < 6) {
    baseline = baseline.subtract(1, "day");
  }

  const endBaseline = baseline.clone().add(24, "hours");
  const now = moment();

  const isCurrentProductionDay =
    now.isSameOrAfter(baseline) && now.isBefore(endBaseline);

  const targetEnd = isCurrentProductionDay
    ? moment.min(now, endBaseline)
    : endBaseline;

  const sorted = timeline
    .map((seg) => {
      const segStart = getMomentFromDateTime(seg.START);
      const segEnd = getMomentFromDateTime(seg.END);

      if (!segStart || !segEnd || !segEnd.isAfter(segStart)) return null;

      const start = moment.max(segStart, baseline);
      const end = moment.min(segEnd, targetEnd);

      if (!end.isAfter(start)) return null;

      return {
        ...seg,
        STATUS: seg.STATUS || seg.PROCESS || "Non-production",
        START: start.format("YYYY-MM-DD HH:mm:ss"),
        END: end.format("YYYY-MM-DD HH:mm:ss"),
        _start: start,
        _end: end,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a._start.diff(b._start));

  const result = [];
  let cursor = baseline.clone();

  sorted.forEach((seg) => {
    if (seg._start.isAfter(cursor)) {
      result.push({
        START: cursor.format("YYYY-MM-DD HH:mm:ss"),
        END: seg._start.format("YYYY-MM-DD HH:mm:ss"),
        STATUS: "Non-production",
      });
    }

    result.push({
      ...seg,
      START: seg._start.format("YYYY-MM-DD HH:mm:ss"),
      END: seg._end.format("YYYY-MM-DD HH:mm:ss"),
    });

    if (seg._end.isAfter(cursor)) {
      cursor = seg._end.clone();
    }
  });

  if (cursor.isBefore(targetEnd)) {
    result.push({
      START: cursor.format("YYYY-MM-DD HH:mm:ss"),
      END: targetEnd.format("YYYY-MM-DD HH:mm:ss"),
      STATUS: "Non-production",
    });
  }

  return result;
};


const MAX_DAY_SECONDS = 24 * 60 * 60;

const clampSecondsTo24H = (seconds) => {
  if (!Number.isFinite(seconds) || seconds < 0) return 0;
  return Math.min(seconds, MAX_DAY_SECONDS);
};

export default function OverviewBar({ oeeDashboard, oeeProcess }) {
  const onColorStatus = (status) => {
    let color;
    if (status === "COMPLETED") {
      color = theme.colorStatus.lightGreen;
    } else if (status === "PROCESSING") {
      color = theme.colorStatus.yellow;
    } else if (status === "WAITING") {
      color = theme.colorStatus.gray;
    }
    return color;
  };

  const onColorBdStatus = (status) => {
    let color;
    if (status === "COMPLETED") {
      color = theme.skuBorderStatus.green;
    } else if (status === "PROCESSING") {
      color = theme.skuBorderStatus.yellow;
    }
    return color;
  };

  const onColorLenStatus = (status) => {
    let color;
    if (status === "green") {
      color = theme.colorStatus.lightGreen;
    } else if (status === "yellow") {
      color = theme.colorStatus.yellow;
    } else if (status === "red") {
      color = theme.colorStatus.red;
    }
    if (!status) {
      color = "transparent";
    }
    return color;
  };

  let sortedItems = (oeeDashboard?.SKU_LIST || []).sort((a, b) => {
    const order = {
      COMPLETED: 1,
      PROCESSING: 2,
      WAITING: 3,
      "": 99,
      undefined: 99,
    };

    return order[a?.status] - order[b?.status];
  });

  while (sortedItems.length < 4) {
    sortedItems.push({});
  }

  sortedItems = sortedItems.slice(0, 4);

  const formatPercent = (val) => {
    if (val === undefined || val === null || val === "-") return "-";
    return Number(val).toFixed(2);
  };

  // const breakdownSummary = calculateBreakdownAMPM(oeeProcess);
  // const nonProductSummary = calculateGroupedNonProductTime(oeeProcess);

  const summaryTimeline = buildTimelineFullDayForSummary(
    oeeProcess,
    oeeDashboard?.PLAN_START
  );

  const nonProductSummary = calculateGroupedNonProductTime(summaryTimeline);

  const toSeconds = (value) => {
    if (value === undefined || value === null || value === "NA") return 0;

    if (moment.isDuration(value)) return value.asSeconds();

    if (typeof value === "string") return moment.duration(value).asSeconds();

    if (typeof value === "number") return value;

    return 0;
  };

  const totalNonProductSeconds =
    nonProductSummary.STOP +
    nonProductSummary.CHANGE_PRD +
    nonProductSummary.BREAK +
    nonProductSummary.CLEANING +
    nonProductSummary.MAT_SHORT +
    nonProductSummary.QUALITY +
    nonProductSummary.OTHER +
    nonProductSummary.RD;

  const totalTimeSeconds = clampSecondsTo24H(
    toSeconds(oeeDashboard?.PRODUCTION_TIME) +
    totalNonProductSeconds +
    toSeconds(oeeDashboard?.MINOR_STOP) +
    toSeconds(oeeDashboard?.BREAKDOWN_TIME)
  );

  const renderRJTooltip = (item) => (
    <Box sx={{ p: 1, minWidth: 120 }}>
      <Box display="flex" justifyContent="space-between">
        <span>Total</span>
        <span>{formatPercent(item?.rj_total)} %</span>
      </Box>
      <Box display="flex" justifyContent="space-between">
        <span>Defect</span>
        <span>{formatPercent(item?.rj_defect)} %</span>
      </Box>
      <Box display="flex" justifyContent="space-between">
        <span>Color</span>
        <span>{formatPercent(item?.rj_color)} %</span>
      </Box>
      <Box display="flex" justifyContent="space-between">
        <span>Shape</span>
        <span>{formatPercent(item?.rj_shape)} %</span>
      </Box>
      <Box display="flex" justifyContent="space-between">
        <span>Dimension</span>
        <span>{formatPercent(item?.rj_dimension)} %</span>
      </Box>
    </Box>
  );

  const renderNonProductTooltip = () => (
    <Box sx={{ p: 1, minWidth: 170 }}>
      <Box display="flex" justifyContent="space-between">
        <span>Stop</span>
        <span>{formatDurationHHmm(nonProductSummary.STOP)}</span>
      </Box>

      <Box display="flex" justifyContent="space-between">
        <span>Change Prd</span>
        <span>{formatDurationHHmm(nonProductSummary.CHANGE_PRD)}</span>
      </Box>

      <Box display="flex" justifyContent="space-between">
        <span>Break</span>
        <span>{formatDurationHHmm(nonProductSummary.BREAK)}</span>
      </Box>

      <Box display="flex" justifyContent="space-between">
        <span>Cleaning</span>
        <span>{formatDurationHHmm(nonProductSummary.CLEANING)}</span>
      </Box>

      <Box display="flex" justifyContent="space-between">
        <span>Mat Short</span>
        <span>{formatDurationHHmm(nonProductSummary.MAT_SHORT)}</span>
      </Box>

      <Box display="flex" justifyContent="space-between">
        <span>Quality</span>
        <span>{formatDurationHHmm(nonProductSummary.QUALITY)}</span>
      </Box>

      <Box display="flex" justifyContent="space-between">
        <span>Other</span>
        <span>{formatDurationHHmm(nonProductSummary.OTHER)}</span>
      </Box>

      <Box display="flex" justifyContent="space-between">
        <span>Rd</span>
        <span>{formatDurationHHmm(nonProductSummary.RD)}</span>
      </Box>
    </Box>
  );

  const renderMinorTooltip = () => (
    <Box sx={{ p: 1, minWidth: 120 }}>
      <Box display="flex" justifyContent="space-between">
        <span>AM</span>
        <span>{oeeDashboard?.MINOR_STOP === undefined || oeeDashboard?.MINOR_STOP === "NA" ? "00:00" : formatDurationHHmm(oeeDashboard?.MINOR_STOP)}</span>
      </Box>
    </Box>
  );

  const renderBreakdownTooltip = () => (
    <Box sx={{ p: 1, minWidth: 120 }}>
      <Box display="flex" justifyContent="space-between">
        <span>PM</span>
        <span>{oeeDashboard?.BREAKDOWN_TIME === undefined || oeeDashboard?.BREAKDOWN_TIME === "NA" ? "00:00" : formatDurationHHmm(oeeDashboard?.BREAKDOWN_TIME)}</span>
      </Box>
    </Box>
  );

  return (
    <>
      <Grid mt={0}>
        <PanelBody>
          <Timeline24H
            timeline={oeeProcess}
            startTime={oeeDashboard?.PLAN_START}
            actualTime={oeeDashboard?.START_TIME}
            endTime={oeeDashboard?.END_TIME}
            flagSrc={`${process.env.PUBLIC_URL}/images/icons/Target.png`}
          />

          <Grid container spacing={1} mt={2} offset={{ lg: 1, md: 0.5, xs: 0 }}>
            <Grid item xs={0.8} display="flex" alignItems="center">
              <img
                className="not-mobile"
                src={`${process.env.PUBLIC_URL}/images/icons/Timeline_TotalTime.png`}
                width={40}
                alt="Timeline_TotalTime"
              />
            </Grid>

            <Grid
              item
              xs={2.2}
              display={"flex"}
              flexDirection={"column"}
              alignItems={"center"}
              sx={{ borderRight: `1px dashed ${theme.colorStatus.gray}` }}
            >
              <Typography fontSize={"clamp(20px,4vw,28px)"}>Total</Typography>
              <Typography
                fontSize={"clamp(24px,4vw,30px)"}
                fontWeight={"bold"}
                color={theme.colorStatus.lightGreen}
              >
                {/* {oeeDashboard?.TOTAL_TIME === undefined || oeeDashboard?.TOTAL_TIME === "NA"
                  ? "-"
                  : formatDurationHHmm(oeeDashboard?.TOTAL_TIME)} */}
                {formatDurationHHmm(totalTimeSeconds)}
              </Typography>
            </Grid>

            <Grid
              item
              xs={2.2}
              display={"flex"}
              flexDirection={"column"}
              alignItems={"center"}
            >
              <Typography fontSize={"clamp(20px,4vw,28px)"}>
                Production
              </Typography>
              <Typography
                fontSize={"clamp(24px,4vw,30px)"}
                fontWeight={"bold"}
                color={theme.colorStatus.green}
              >
                {oeeDashboard?.PRODUCTION_TIME === undefined || oeeDashboard?.PRODUCTION_TIME === "NA"
                  ? "-"
                  : formatDurationHHmm(oeeDashboard?.PRODUCTION_TIME)}
              </Typography>
            </Grid>

            <Tooltip
              title={renderNonProductTooltip()}
              arrow
              placement="top"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: "#000",
                    color: "#fff",
                    fontSize: 16,
                    borderRadius: "6px",
                  },
                },
                arrow: {
                  sx: {
                    color: "#000",
                  },
                },
              }}
            >
              <Grid
                item
                xs={2.4}
                display={"flex"}
                flexDirection={"column"}
                alignItems={"center"}
                sx={{ cursor: "pointer" }}
              >
                <Typography fontSize={"clamp(20px,4vw,27px)"}>
                  Non-Production
                </Typography>
                <Typography
                  fontSize={"clamp(24px,4vw,30px)"}
                  fontWeight={"bold"}
                  color={theme.colorStatus.yellow}
                >
                  {/* {oeeDashboard?.NON_PRODUCTION_TIME === undefined || oeeDashboard?.NON_PRODUCTION_TIME === "NA"
                    ? "-"
                    : formatDurationHHmm(oeeDashboard?.NON_PRODUCTION_TIME)} */}
                  {formatDurationHHmm(totalNonProductSeconds)}
                </Typography>
              </Grid>
            </Tooltip>

            <Tooltip
              title={renderMinorTooltip()}
              arrow
              placement="top"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: "#000",
                    color: "#fff",
                    fontSize: 16,
                    borderRadius: "6px",
                  },
                },
                arrow: {
                  sx: {
                    color: "#000",
                  },
                },
              }}
            >
              <Grid
                item
                xs={2.2}
                display={"flex"}
                flexDirection={"column"}
                alignItems={"center"}
              >
                <Typography fontSize={"clamp(20px,4vw,28px)"}>
                  Minor Stop
                </Typography>
                <Typography
                  fontSize={"clamp(24px,4vw,30px)"}
                  fontWeight={"bold"}
                  color={theme.colorStatus.orange}
                >
                  {oeeDashboard?.MINOR_STOP === undefined || oeeDashboard?.MINOR_STOP === "NA"
                    ? "-"
                    : formatDurationHHmm(oeeDashboard?.MINOR_STOP)}
                </Typography>
              </Grid>
            </Tooltip>

            <Tooltip
              title={renderBreakdownTooltip()}
              arrow
              placement="top"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: "#000",
                    color: "#fff",
                    fontSize: 16,
                    borderRadius: "6px",
                  },
                },
                arrow: {
                  sx: {
                    color: "#000",
                  },
                },
              }}
            >
              <Grid
                item
                xs={2.2}
                display={"flex"}
                flexDirection={"column"}
                alignItems={"center"}
                sx={{ cursor: "pointer" }}
              >
                <Typography fontSize={"clamp(20px,4vw,28px)"}>
                  Breakdown
                </Typography>
                <Typography
                  fontSize={"clamp(24px,4vw,30px)"}
                  fontWeight={"bold"}
                  color={theme.colorStatus.red}
                >
                  {oeeDashboard?.BREAKDOWN_TIME === undefined || oeeDashboard?.BREAKDOWN_TIME === "NA"
                    ? "-"
                    : formatDurationHHmm(oeeDashboard?.BREAKDOWN_TIME)}
                </Typography>
              </Grid>
            </Tooltip>
          </Grid>
        </PanelBody>
      </Grid>

      <Grid mt={2}>
        <PanelBody>
          <ProgressFG
            actual={oeeDashboard?.ACTUAL_KG === undefined || oeeDashboard?.ACTUAL_KG === "NA" ? 0 : oeeDashboard?.ACTUAL_KG}
            previousTarget={oeeDashboard?.ACTUAL_PREPARATION_KG === undefined || oeeDashboard?.ACTUAL_PREPARATION_KG === "NA" ? 0 : Math.round(oeeDashboard?.ACTUAL_PREPARATION_KG)}
            target={oeeDashboard?.TARGET_KG === undefined || oeeDashboard?.TARGET_KG === "NA" ? 0 : oeeDashboard?.TARGET_KG}
            flagPositionValue={oeeDashboard?.TARGET_NOW === undefined || oeeDashboard?.TARGET_NOW === "NA" ? 0 : Math.round(oeeDashboard?.TARGET_NOW)}
          />

          <PanelSKUWrapper>
            <Grid container alignItems={"center"}>
              <Grid item md={2.4} xs={12}>
                <BoxValue sx={{ textAlign: "center" }}>Skus</BoxValue>
              </Grid>
              <Grid item md={2.4} xs={3}>
                <LabelSku sx={{ textAlign: "center" }}>
                  <span>Target:</span>
                  <span>{oeeDashboard?.SKU_TARGET}</span>
                </LabelSku>
              </Grid>
              <Grid item md={2.4} xs={3}>
                <LabelSku sx={{ textAlign: "center" }}>
                  <span>Completed:</span>
                  <span style={{ color: theme.colorStatus.lightGreen }}>
                    {oeeDashboard?.SKU_COMPLETED}
                  </span>
                </LabelSku>
              </Grid>
              <Grid item md={2.4} xs={3}>
                <LabelSku sx={{ textAlign: "center" }}>
                  <span>On procress:</span>
                  <span style={{ color: theme.colorStatus.yellow }}>
                    {oeeDashboard?.SKU_PROCESSING}
                  </span>
                </LabelSku>
              </Grid>
              <Grid item md={2.4} xs={3}>
                <LabelSku sx={{ textAlign: "center" }}>
                  <span>Waiting:</span>
                  <span style={{ color: theme.colorStatus.gray }}>
                    {oeeDashboard?.SKU_WAITING}
                  </span>
                </LabelSku>
              </Grid>
            </Grid>

            <Grid container spacing={2} m={0}>
              {sortedItems.map((item, index) => (
                <Grid key={index} item md={2.9} xs={6}>
                  {item.sku ? (
                    <BoxSku
                      color={onColorStatus(item.status)}
                      bdcolor={onColorBdStatus(item.status)}
                    >
                      <Typography>{item.sku}</Typography>

                      <Grid sx={{ color: `${theme.palette.text.gray}` }}>
                        <Box>
                          <Box>
                            <span>FG</span>
                            {item.status === "COMPLETED" ? (
                              <StatusCircle color={onColorLenStatus(item.fg_color)} />
                            ) : (
                              ""
                            )}
                          </Box>

                          <Box>
                            <span>MY</span>
                            {item.status === "COMPLETED" ? (
                              <StatusCircle color={onColorLenStatus(item.my_color)} />
                            ) : (
                              ""
                            )}
                          </Box>

                          <Tooltip
                            title={renderRJTooltip(item)}
                            arrow
                            placement="top"
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  bgcolor: "#000",
                                  color: "#fff",
                                  fontSize: 12,
                                  borderRadius: "6px",
                                },
                              },
                              arrow: {
                                sx: {
                                  color: "#000",
                                },
                              },
                            }}
                          >
                            <Box sx={{ display: "contents" }}>
                              <Box>
                                <span>RJ</span>
                                {item.status === "COMPLETED" ? (
                                  <StatusCircle color={onColorLenStatus(item.rj_color)} />
                                ) : (
                                  ""
                                )}
                              </Box>
                            </Box>
                          </Tooltip>

                          <Box>
                            <span>GW</span>
                            {item.status === "COMPLETED" ? (
                              <StatusCircle color={onColorLenStatus(item.gw_color)} />
                            ) : (
                              ""
                            )}
                          </Box>
                        </Box>

                        <Box
                          sx={{
                            alignItems: "end !important",
                          }}
                        >
                          <Box sx={{ justifyContent: "end !important" }}>
                            <span>
                              <ValueSkg color={onColorStatus(item.status)}>
                                {item?.fg === undefined || item?.fg === "-"
                                  ? "-"
                                  : Math.round(item?.fg)?.toLocaleString() || "-"}
                              </ValueSkg>
                              kg
                            </span>
                          </Box>

                          <Box sx={{ justifyContent: "end !important" }}>
                            <span>
                              <ValueSkg color={onColorStatus(item.status)}>
                                {item?.my === undefined || item?.my === "-"
                                  ? "-"
                                  : Number(item?.my)?.toFixed(2) || "-"}
                              </ValueSkg>
                              %
                            </span>
                          </Box>

                          <Tooltip
                            title={renderRJTooltip(item)}
                            arrow
                            placement="top"
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  bgcolor: "#000",
                                  color: "#fff",
                                  fontSize: 12,
                                  borderRadius: "6px",
                                },
                              },
                              arrow: {
                                sx: {
                                  color: "#000",
                                },
                              },
                            }}
                          >
                            <Box sx={{ display: "contents" }}>
                              <Box sx={{ justifyContent: "end !important" }}>
                                <span>
                                  <ValueSkg color={onColorStatus(item.status)}>
                                    {item?.rj === undefined || item?.rj === "-"
                                      ? "-"
                                      : item?.rj === "0"
                                        ? 0
                                        : Number(item?.rj)?.toFixed(2)}
                                  </ValueSkg>
                                  %
                                </span>
                              </Box>
                            </Box>
                          </Tooltip>

                          <Box sx={{ justifyContent: "end !important" }}>
                            <span>
                              <ValueSkg color={onColorStatus(item.status)}>
                                {item?.gw === undefined || item?.gw === "-"
                                  ? "-"
                                  : Number(item?.gw)?.toFixed(2) || "-"}
                              </ValueSkg>
                              %
                            </span>
                          </Box>
                        </Box>
                      </Grid>
                    </BoxSku>
                  ) : (
                    <BoxValue sx={{ height: "167px" }} />
                  )}
                </Grid>
              ))}
            </Grid>
          </PanelSKUWrapper>
        </PanelBody>
      </Grid>
    </>
  );
}